<template>
  <span
    class="view-number d-inline-flex align-items-center flex-wrap gap-1"
    :class="{ 'currency-display': field.currency }"
    :title="fieldTooltipTitle"
  >
    <!-- Si es array, mostrar separado por comas -->
    <span v-if="field.isArray && Array.isArray(displayValue)">
      <span
        v-for="(val, index) in displayValue"
        :key="index"
        class="number-item"
      >
        <span v-if="field.currency" class="currency-value">
          {{ formatCurrencyForDisplay(val) }}
          <span class="currency-symbol">{{ getCurrencySymbolForValue(val) }}</span>
        </span>
        <span v-else>
          {{ formatNumberForDisplay(val) }}
        </span>
        <span v-if="index < displayValue.length - 1">, </span>
      </span>
    </span>
    <!-- Valor simple con currency -->
    <span v-else-if="field.currency" class="currency-value">
      {{ formatCurrencyForDisplay(displayValue) }}
      <span class="currency-symbol">{{ currencySymbol }}</span>
    </span>
    <!-- Valor simple sin currency -->
    <span v-else>
      {{ formatNumberForDisplay(displayValue) }}
    </span>
    <FindTeuTrackingViewButton
      v-if="!isGroupModel"
      :field-key="field.key"
      :raw-value="displayValue"
      :model-name="modelName"
    />
  </span>
</template>

<script>
import { formatNumberSync, getDisplayDecimalsOverrideForFieldKey } from "@/utils/numberFormatter";
import FindTeuTrackingViewButton from "@/components/findTeu/FindTeuTrackingViewButton.vue";
import { currencyLabelFor, looksLikeObjectId } from "@/utils/currencyLabelCache";

export default {
  name: "ViewFieldNumber",
  components: {
    FindTeuTrackingViewButton,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [Number, String, Array],
      default: null,
    },
    item: {
      type: Object,
      default: null,
    },
    parentItem: {
      type: Object,
      default: null,
      validator: (value) => value === null || typeof value === 'object',
    },
    /** Fila del grupo que se está mostrando (subform), si la hay. */
    rowItem: {
      type: Object,
      default: null,
    },
    modelName: {
      type: String,
      default: "",
    },
    isGroupModel: {
      type: Boolean,
      default: false,
    },
    lists: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    displayValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        return this.value;
      }
      if (this.field.defaultValue !== undefined && this.field.defaultValue !== null) {
        return this.field.defaultValue;
      }
      return null;
    },
    fieldTooltipTitle() {
      const tip = this.field && this.field.tooltip;
      if (tip == null || String(tip).trim() === "") return undefined;
      return String(tip);
    },
    currencySymbol() {
      if (!this.field.currency || (!this.field.currencyField && !this.field.currencyFixed)) {
        return null;
      }

      let currencyValue = null;
      // Moneda fija del campo (p. ej. total de gastos en la moneda del sistema, #397).
      if (this.field.currencyFixed) {
        currencyValue = this.field.currencyFixed;
      // Fila de un grupo (un gasto del embarque): su propia moneda antes que la del padre.
      } else if (this.rowItem && this.rowItem[this.field.currencyField] !== undefined) {
        currencyValue = this.rowItem[this.field.currencyField];
      } else if (this.item && this.item[this.field.currencyField] !== undefined) {
        currencyValue = this.item[this.field.currencyField];
      } else if (this.parentItem && this.parentItem[this.field.currencyField] !== undefined) {
        currencyValue = this.parentItem[this.field.currencyField];
      }

      if (
        currencyValue === null ||
        currencyValue === undefined ||
        currencyValue === ""
      ) {
        return null;
      }

      if (typeof currencyValue === "object" && currencyValue.label) {
        return currencyValue.label;
      }

      const currencyId = typeof currencyValue === "object" && currencyValue._id 
        ? currencyValue._id 
        : currencyValue;

      if (currencyId && this.lists && this.lists.length > 0) {
        const currencyList = this.lists.find((l) => l.type === "currency");
        if (currencyList && currencyList.values) {
          const findValueInTree = (values, targetId) => {
            for (const item of values) {
              if (
                item._id?.toString() === targetId?.toString() ||
                item._id === targetId
              ) {
                return item.label;
              }
              if (item.children && item.children.length > 0) {
                const found = findValueInTree(item.children, targetId);
                if (found) return found;
              }
            }
            return null;
          };

          const label = findValueInTree(currencyList.values, currencyId);
          if (label) {
            return label;
          }
        }
      }

      // La vista no trajo la lista de monedas: el nombre sale del caché del tenant. Nunca se
      // muestra el id interno como si fuera la moneda.
      if (currencyId && looksLikeObjectId(currencyId)) {
        return currencyLabelFor(currencyId);
      }

      return typeof currencyValue === "object" ? "" : String(currencyValue);
    },
  },
  methods: {
    formatNumberForDisplay(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const decimals = getDisplayDecimalsOverrideForFieldKey(this.field?.key);
      return formatNumberSync(value, null, false, decimals);
    },
    formatCurrencyForDisplay(value) {
      if (value === null || value === undefined || value === "") {
        return "";
      }
      const decimals = getDisplayDecimalsOverrideForFieldKey(this.field?.key);
      return formatNumberSync(value, null, this.field.currency || false, decimals);
    },
    getCurrencySymbolForValue(value) {
      return this.currencySymbol;
    },
  },
};
</script>

<style scoped>
.view-number {
  font-weight: 500;
}

.currency-display {
  font-family: "Courier New", monospace;
  font-weight: 500;
  color: #2c3e50;
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e9ecef;
  display: inline-block;
  min-width: 80px;
  text-align: right;
}

.currency-display:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.currency-value {
  font-weight: 600;
}

.currency-symbol {
  margin-left: 4px;
  font-weight: 500;
}

.number-item {
  display: inline;
}
</style>

